// CH-37826 — HttpOnly-cookie session auth ("cookie mode").
//
// The browser holds only an opaque `_is4` cookie. The bearer token stays inside the
// cluster: this app's own nginx runs auth_request against the auth BFF, which returns
// the bearer in an X-Auth-Bearer response header, and nginx attaches it before proxying
// to the gateway. So in cookie mode the SPA sends NO Authorization header and calls
// same-origin paths instead of the absolute gateway URLs.
//
// Ported from apps/persona-portal/src/app/auth/ (the lean variant). Defaults OFF: every
// host without `cookieModeEnabled: true` in urlHostToConfigsMap keeps the legacy
// localStorage-token flow, unchanged.
//
// Deliberately dependency-free — reads only window, localStorage and fetch. No Vuex, no
// router. That is what makes it testable without mocking half the app.

const SESSION_ACTIVE_KEY = "cg_session_active"
const BFF_BASE_URL = "/auth-bff"
// Studio stays unmounted until the boot session check settles; nginx alone would wait 60 s.
const SESSION_STATUS_TIMEOUT_MS = 3000

function isLocalhost() {
	return /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/.test(window.location?.host || "")
}

// nginx resolves tenantId and clientId from the REAL Host header, never from the URL.
// decideHostFromUrl() honours ?hostOverride= AND persists it to localStorage, so on every
// later load the resolved config key can be a different host with no override in the URL.
// Comparing the resolved key (not the query param) is what catches those later loads.
function isHostMismatched() {
	return window.envConfig?.RESOLVED_HOST !== window.location?.host
}

// "This host is configured for cookie mode." True from first paint, guest included.
// Decides HOW LOGIN WORKS.
export function isCookieModeActive() {
	if (isHostMismatched()) return false
	// A dev server proxies none of the same-origin paths cookie mode depends on, and
	// urlHostToConfigsMap allows local host aliases, so a flagged tenant could otherwise
	// turn cookie mode on against a server that cannot serve it. Opt in explicitly.
	if (isLocalhost() && process.env.VUE_APP_COOKIE_MODE_ENABLED !== "true") return false
	return window.envConfig?.COOKIE_MODE_ENABLED === true
}

// UI hint only, never a credential — the session lives in the HttpOnly cookie, so forging
// this can at worst mis-route the SPA into a request the gate then rejects.
export function setSessionActiveFlag() {
	localStorage.setItem(SESSION_ACTIVE_KEY, "true")
}

export function clearSessionActiveFlag() {
	localStorage.removeItem(SESSION_ACTIVE_KEY)
}

export function isSessionActiveFlagSet() {
	return localStorage.getItem(SESSION_ACTIVE_KEY) === "true"
}

// "…AND a session exists." Decides WHERE TRAFFIC GOES. The split from
// isCookieModeActive() is the guest bypass: without it every unauthenticated page would
// hit the auth_request gate, 401, and report a session expiry to someone who never
// logged in.
export function isCookieSessionActive() {
	return isCookieModeActive() && isSessionActiveFlagSet()
}

// CH-52544: a pure read. The latch lives in initApp's initEnvConfig(), the single point where
// every cookie-mode downgrade has already been applied, so re-deriving it here would be a
// second copy of the rule to drift.
export function isCodeFlowLoginActive() {
	return window.envConfig?.LOGIN_MODE === "CodeFlow"
}

export function applyCookieModeEndpoints() {
	if (!isCookieSessionActive()) return

	if (!window.envConfig.COVERGO_FILE_SYSTEM_DIRECT) {
		// Kept for CRichTextEditor: its <img src> URLs are PERSISTED into saved template
		// HTML and re-rendered on other origins, where a relative path is dead.
		window.envConfig.COVERGO_FILE_SYSTEM_DIRECT = window.envConfig.COVERGO_FILE_SYSTEM
	}

	window.envConfig.COVERGO_GRAPHQL = "/graphql"
	window.envConfig.COVERGO_FILE_SYSTEM = "/api/v1/files"
}

// ponytail: no revertCookieModeEndpoints — every Studio logout path ends in a full page
// load, which re-runs initApp and re-derives endpoints from the tenant map. Add one if
// Studio ever gains an in-SPA logout or a guest route that calls the API.

export function bearerHeaders(token) {
	if (isCookieSessionActive()) return {}
	return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function cookieLogin({ tenantId, clientId, username, password }) {
	// tenantId in the path is cosmetic — nginx re-derives both tenantId and clientId from
	// the real Host header and appends ?clientId=. It is the route shape the BFF matches on.
	const response = await fetch(`${BFF_BASE_URL}/api/auth/${tenantId}/is4-bridge/login`, {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ clientId, username, password }),
	})

	if (!response.ok) throw new Error("Login failed")

	const body = await response.json()
	// Studio has no OTP UI anywhere. Fail loudly rather than reporting a success the user
	// has no way to complete.
	if (body?.requiresTwoFactor) throw new Error("Two-factor authentication is not supported in Studio")

	// No tokens are ever returned — the BFF sets the HttpOnly cookie itself.
	localStorage.removeItem(process.env.VUE_APP_TOKEN_KEY)
	localStorage.removeItem(process.env.VUE_APP_REFRESH_TOKEN_KEY)
	setSessionActiveFlag()
	applyCookieModeEndpoints()

	return body
}

// CH-52544: hosted login. A top-level document navigation, not a fetch — the BFF answers with
// a cross-origin 302 to IdentityServer that only the browser can follow. The SPA is gone after
// this line, so returnUrl has to be a real query param: in-memory state does not survive it.
export function startCodeFlowLogin(tenantId, clientId, returnUrl) {
	// URLSearchParams, not string concat: a clientId carrying "&returnUrl=/evil" would
	// otherwise inject a second parameter.
	const params = new URLSearchParams({ clientId })
	// Site-relative, single slash only. The BFF re-validates — it owns the open-redirect
	// boundary — but an absolute URL should never leave Studio in the first place. The
	// backslash is excluded too: WHATWG resolves `/\evil.example` to https://evil.example.
	if (typeof returnUrl === "string" && /^\/(?![/\\])/.test(returnUrl)) params.set("returnUrl", returnUrl)
	// CH-53136: always. An expired login or a logout with no BFF session leaves IS4's own
	// session alive, and without this IS4 signs the user straight back in.
	params.set("prompt", "login")

	// Optimistic: the callback is a server redirect with no post-return hook to set it in.
	// verifyCodeFlowSession() drops it on the next boot if the login never completed.
	setSessionActiveFlag()
	window.location.assign(`${BFF_BASE_URL}/api/auth/${encodeURIComponent(tenantId)}/is4-bridge/authorize?${params}`)
}

// CH-53136: startCodeFlowLogin sets the marker before the IdP hop, so a failed or abandoned
// login leaves it behind. Only hosted-login hosts pay this one boot round-trip.
export async function verifyCodeFlowSession(tenantId) {
	if (!isCodeFlowLoginActive() || !isSessionActiveFlagSet() || !tenantId) return

	const controller = new AbortController()
	const timer = setTimeout(() => controller.abort(), SESSION_STATUS_TIMEOUT_MS)
	try {
		const response = await fetch(`${BFF_BASE_URL}/api/auth/${tenantId}/is4-bridge/session-status`, {
			credentials: "include",
			signal: controller.signal,
		})
		// Only a definite "no session". A network blip keeps the marker; the 401 handler catches it later.
		if (response.status === 401) cookieSessionExpired()
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("[cookieMode] session-status request failed", error)
	} finally {
		clearTimeout(timer)
	}
}

export async function cookieLogout(tenantId) {
	if (!isCookieModeActive() || !tenantId) return null

	try {
		// Awaited, not fire-and-forget: every caller assigns window.location straight
		// after, which would cancel the request and leave the server-side session live.
		const response = await fetch(`${BFF_BASE_URL}/api/auth/${tenantId}/is4-bridge/logout`, {
			method: "POST",
			credentials: "include",
		})

		// CH-52544: clearing our own cookie leaves IdentityServer's `idsrv` alive, and the next
		// /authorize would mint a code against it — a sign-out that does not sign out. Only a
		// top-level navigation to the end-session URL kills it. 204 is the ROPC answer, whose
		// session never had an IdentityServer cookie; .json() on that empty body would throw.
		if (response.ok && response.status !== 204) {
			const body = await response.json()
			return body?.endSessionUrl ?? null
		}
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("[cookieMode] logout request failed", error)
	} finally {
		clearSessionActiveFlag()
	}

	return null
}

// Reconcile a dead session. The full reload re-runs initApp; with the marker gone
// applyCookieModeEndpoints() no-ops and endpoints resolve back to the direct gateway.
export function cookieSessionExpired() {
	clearSessionActiveFlag()
	window.location = "/"
}
